import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { youtubeService } from '../services/youtubeService';
import './VideoCard.css';

const VideoCard = ({ video }) => {
  const navigate = useNavigate();
  const [channelThumbnail, setChannelThumbnail] = useState(null);

  const getVideoId = () => {
    return video.id.videoId || video.id;
  };

  const getThumbnail = () => {
    return video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url;
  };

  const formatViews = (views) => {
    if (!views) return '';
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M views`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(0)}K views`;
    }
    return `${views} views`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatDuration = (duration) => {
    if (!duration) return '';
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    const hours = (match[1] || '').replace('H', '');
    const minutes = (match[2] || '').replace('M', '');
    const seconds = (match[3] || '').replace('S', '');

    if (hours) {
      return `${hours}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
    }
    return `${minutes || '0'}:${seconds.padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchChannelThumbnail = async () => {
      if (video?.snippet?.channelId) {
        try {
          const channel = await youtubeService.getChannelDetails(video.snippet.channelId);
          if (channel?.snippet?.thumbnails?.default?.url) {
            setChannelThumbnail(channel.snippet.thumbnails.default.url);
          }
        } catch (error) {
          console.error('Error fetching channel thumbnail:', error);
        }
      }
    };
    fetchChannelThumbnail();
  }, [video?.snippet?.channelId]);

  const handleClick = () => {
    navigate(`/video/${getVideoId()}`);
  };

  return (
    <div className="video-card" onClick={handleClick}>
      <div className="video-thumbnail">
        <img src={getThumbnail()} alt={video.snippet.title} loading="lazy" />
        {video.contentDetails?.duration && (
          <div className="video-duration">
            {formatDuration(video.contentDetails.duration)}
          </div>
        )}
        <div className="video-overlay">
          <svg viewBox="0 0 24 24" width="48" height="48" className="play-icon">
            <path d="M8 5v14l11-7z" fill="white"/>
          </svg>
        </div>
      </div>
      <div className="video-info">
        <div className="video-avatar">
          {channelThumbnail ? (
            <img src={channelThumbnail} alt={video.snippet.channelTitle} />
          ) : (
            video.snippet.channelTitle.charAt(0).toUpperCase()
          )}
        </div>
        <div className="video-details">
          <h3 className="video-title">{video.snippet.title}</h3>
          <div className="video-channel">{video.snippet.channelTitle}</div>
          <div className="video-stats">
            {video.statistics?.viewCount && (
              <>
                {formatViews(video.statistics.viewCount)} • 
              </>
            )}
            {formatDate(video.snippet.publishedAt)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
